import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Bredcrum from '../../common/Bredcrum';
import Loader from '../../common/Loader';
import Accordion from '../Faqs/Accordian';
import { axiosInstance, config } from '../config';
import { splitDescriptionHtml } from './courseDescriptionSections';
import { getCourseDaysLabel } from './courseFilterUtils';
import { getCourseSlug, findCourseBySlug } from './courseSlugs';
import { FallbackImage, getCourseImageSrc } from './courseImages';

import detailBannerImg from '../../../assets/images/courses/banner_detail.png';
import '../../../assets/styles/coursesHero.css';
import '../../../assets/styles/courseDetails.css';

const CourseDetails = () => {
  const [courseData, setCourseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { course_slug } = useParams();

  useEffect(() => {
    getCourseInfo();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course_slug]);

  function getCourseInfo() {
    setLoading(true);

    // course_slug is usually a readable slug (e.g. "3-day-automatic-intensive"),
    // matched against the full course list. If it's a plain numeric id
    // (old bookmarked links), fetch that course directly instead.
    if (/^\d+$/.test(course_slug)) {
      axiosInstance
        .get(`get-cources/${course_slug}`)
        .then((res) => {
          setCourseData(res?.data?.cource || null);
        })
        .catch((err) => console.log(err))
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    axiosInstance
      .get(`get-cources`)
      .then((res) => {
        const courses = res?.data?.cources || [];
        const match = findCourseBySlug(courses, course_slug);
        setCourseData(match);
      })
      .catch((err) => console.log(err))
      .finally(() => {
        setLoading(false);
      });
  }

  if (loading || !courseData) {
    return <Loader />;
  }

  const daysLabel = getCourseDaysLabel(courseData);
  const hasTheory = Number(courseData.theory) > 0;
  const imageSrc = getCourseImageSrc(courseData, config.imageUrl);
  // The admin panel's editor saves HTML. Older courses were saved as plain
  // text, where each line becomes a paragraph.
  const rawDescription = `${courseData.description || ''}`;
  const descriptionIsHtml = /<\/?[a-z][\s\S]*>/i.test(rawDescription);
  const descriptionParagraphs = (
    descriptionIsHtml
      ? rawDescription
          .replace(/<\/(p|div|li|h[1-6]|br)>|<br\s*\/?>/gi, '\n')
          .replace(/<[^>]*>/g, '')
          .replace(/&nbsp;/g, ' ')
          .replace(/&amp;/g, '&')
      : rawDescription
  )
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const shortDescription = descriptionParagraphs[0] || '';
  const descriptionSections = descriptionIsHtml
    ? splitDescriptionHtml(rawDescription)
    : [];

  return (
    <>
      <section className="course-detail-hero">
        <img src={detailBannerImg} alt="" className="course-detail-hero-img" />
      </section>

      <Bredcrum
        heading={courseData.title}
        path={[
          { name: 'Courses', path: 'courses' },
          { name: courseData.title, path: `courses/${getCourseSlug(courseData)}` },
        ]}
        variant="highlighted"
      />

      <section className="course-detail-title-bar">
        <div className="auto-container">
          <span className="course-detail-hero-tag">
            {courseData.cartype} Course
          </span>
          <h1>{courseData.title}</h1>
          <div className="course-detail-hero-badges">
            <span className="course-detail-badge">
              <i className="bi bi-clock-fill"></i>
              {courseData.practical} Hours Practical
            </span>
            {daysLabel && (
              <span className="course-detail-badge">
                <i className="bi bi-calendar2-week-fill"></i>
                {daysLabel}
              </span>
            )}
            <span className="course-detail-badge price">
              <i className="bi bi-currency-pound"></i>
              {courseData.price}
            </span>
          </div>
        </div>
      </section>

      <section className="course-detail-body">
        <div className="auto-container">
          <div className="course-detail-layout">
            <div className="course-detail-main">
              {descriptionIsHtml ? (
                descriptionSections.map((section, index) => (
                  <div
                    key={index}
                    className={`course-detail-section${index === 0 ? ' course-detail-intro' : ''}${section.faqs.length ? ' course-detail-faqs' : ''}`}
                  >
                    {index === 0 && !section.title && (
                      <span className="course-detail-eyebrow">
                        {courseData.cartype} Course
                      </span>
                    )}
                    <h2>{section.title || 'About This Course'}</h2>
                    {section.html && (
                      <div
                        className="course-detail-rich"
                        dangerouslySetInnerHTML={{ __html: section.html }}
                      />
                    )}
                    {section.chips.length > 0 && (
                      <div className="course-learn-grid">
                        {section.chips.map((item, chipIndex) => (
                          <div key={chipIndex} className="course-learn-chip">
                            <i className="bi bi-check2-square"></i>
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {section.faqs.length > 0 && (
                      <Accordion
                        questions={section.faqs}
                        className="course-detail-accordion-question"
                      />
                    )}
                  </div>
                ))
              ) : (
                <div className="course-detail-section course-detail-intro">
                  <span className="course-detail-eyebrow">
                    {courseData.cartype} Course
                  </span>
                  <h2>About This Course</h2>
                  {descriptionParagraphs.map((para, index) => (
                    <p key={index}>{para}</p>
                  ))}
                </div>
              )}
            </div>

            <aside className="course-detail-sidebar">
              <div className="course-summary-card">
                <div className="course-summary-image">
                  <img
                    src={imageSrc}
                    alt={courseData.title}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = FallbackImage;
                    }}
                  />
                  <span className="course-summary-transmission">
                    {courseData.cartype}
                  </span>
                </div>
                <div className="course-summary-body">
                  <div className="course-summary-price">
                    <span className="symble">£</span>
                    {courseData.price}
                  </div>
                  {shortDescription && (
                    <p className="course-summary-tagline">
                      {shortDescription.length > 180
                        ? `${shortDescription.slice(0, 177)}...`
                        : shortDescription}
                    </p>
                  )}
                  <ul className="course-summary-facts">
                    <li>
                      <i className="bi bi-clock-fill"></i>
                      {courseData.practical} Hours Practical Driving
                    </li>
                    {hasTheory && (
                      <li>
                        <i className="bi bi-book-fill"></i>
                        {courseData.theory} Hours Theory
                      </li>
                    )}
                    {daysLabel && (
                      <li>
                        <i className="bi bi-calendar2-week-fill"></i>
                        {daysLabel}
                      </li>
                    )}
                  </ul>
                  <a
                    href={`${config.appUrl}book-a-lesson`}
                    className="course-summary-cta"
                  >
                    Book Now
                    <i className="bi bi-arrow-right"></i>
                  </a>
                  <div className="course-summary-phone">
                    <i className="bi bi-telephone-fill"></i>
                    <div>
                      <span>Call to book</span>
                      <a href="tel:01162102688">0116 210 2688</a>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      <section className="course-detail-cta">
        <div className="auto-container">
          <h2>Ready to Get Started?</h2>
          <p>
            Book your {courseData.title} today and start learning with
            Precision Driving School.
          </p>
          <a
            href={`${config.appUrl}book-a-lesson`}
            className="course-detail-cta-btn"
          >
            Book This Course
            <i className="bi bi-arrow-right"></i>
          </a>
        </div>
      </section>
    </>
  );
};

export default CourseDetails;
